import type { ReportOption, ReportPeriod } from '@/models/reportFilterTypes';

/** Mirrors `lists.billedOptions` (`services/ListsService`). */
export const BILLED_OPTIONS: ReportOption[] = [
  { id: 'Final', label: 'Billed' },
  { id: 'Interim', label: 'Draft' },
  { id: 'WIP', label: 'WIP' },
  { id: 'notFinal', label: 'Not Billed' },
  { id: 'all', label: 'All' },
];

/**
 * The standard reports label these "Exclude/Include/Only Archived" and key them on
 * `status`; the generic engine used the same three with the same ids.
 */
export const ARCHIVED_OPTIONS: ReportOption[] = [
  { id: 'active', label: 'Exclude Archived' },
  { id: 'all', label: 'Include Archived' },
  { id: 'archived', label: 'Only Archived' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const monthString = (month: number): string => MONTHS[month];

export const shortMonthString = (month: number): string => MONTHS[month].slice(0, 3);

/**
 * Mirrors `filterService.periods(short)` exactly, including the entries with no `id`
 * (which is why report deep links match on `id` *or* `label`) and the deliberately
 * null-bounded "All Time" and "… and earlier" ranges.
 *
 * Built per call because every range is relative to today, and because the standard
 * reports mutate the period they select.
 */
export function reportPeriods(short = false, now: Date = new Date()): ReportPeriod[] {
  const year = now.getFullYear();
  const month = now.getMonth();
  const date = now.getDate();
  const day = now.getDay();

  const endOfPreviousMonth = new Date(year, month, 0);
  const previousMonthLabel = `${
    short ? shortMonthString(endOfPreviousMonth.getMonth()) : monthString(endOfPreviousMonth.getMonth())
  } ${endOfPreviousMonth.getFullYear()}`;
  const startOfThisWeek = new Date(year, month, date - day);
  const endOfWeek = new Date(year, month, date - day + 7);
  const startOfLastWeek = new Date(year, month, date - day - 7);
  const today = new Date(year, month, date);

  return [
    { id: 'thisMonth', label: 'This Month', range: { startDate: new Date(year, month, 1), endDate: new Date(year, month + 1, 0) } },
    { id: 'all', label: 'All Time', range: { startDate: null, endDate: null } },
    { id: 'thisYear', label: 'This Year', range: { startDate: new Date(year, 0, 1), endDate: new Date(year, 11, 31) } },
    { id: 'lastYear', label: 'Last Year', range: { startDate: new Date(year - 1, 0, 1), endDate: new Date(year - 1, 11, 31) } },
    { label: 'This Week', range: { startDate: startOfThisWeek, endDate: endOfWeek } },
    { label: 'Last Week', range: { startDate: startOfLastWeek, endDate: startOfThisWeek } },
    { label: 'Today', range: { startDate: today, endDate: today } },
    { label: `${previousMonthLabel} and earlier`, range: { startDate: null, endDate: endOfPreviousMonth } },
    {
      id: 'lastMonth',
      label: previousMonthLabel,
      range: {
        startDate: new Date(endOfPreviousMonth.getFullYear(), endOfPreviousMonth.getMonth(), 1),
        endDate: endOfPreviousMonth,
      },
    },
    { label: 'Custom Period', custom: true, range: { startDate: today, endDate: today } },
  ];
}

/** Every capability on, which is what the standard reports effectively assume. */
export const ALL_REPORT_FILTERS = Object.freeze({
  dateRange: true,
  matterForUser: true,
  archivedStatus: true,
  matterTags: true,
  billed: true,
  contingencyBilled: true,
  distribution: true,
  trustAccount: true,
  clientMatter: true,
});

/** Debounce for filter changes — it exists to stop the report flashing on rapid edits. */
export const REPORT_FILTER_DEBOUNCE_MS = 300;
